import axios from "axios";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";

import User from "../models/User";

const API_BASE_URL = "https://europe-west3-hyper-e122e.cloudfunctions.net";

function detectPlatform() {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return "ios";
  if (/Android/.test(navigator.userAgent)) return "android";
  return "web";
}

const UserService = {

  async createWorkerProfile(userDataPartial = {}) {
    const auth = getAuth();
    const currentUser = auth.currentUser;
  
    if (!currentUser) {
      const e = new Error("Пользователь не авторизован");
      e.code = "NO_AUTH";
      throw e;
    }
  
    const uid = currentUser.uid;
    const token = await currentUser.getIdToken(true);
  
    const payload = {
      user_id: uid,
      platform: detectPlatform(),
      ...userDataPartial,
    };
  
    console.log("📤 [createWorkerProfile] payload:", JSON.stringify(payload, null, 2));
  
    try {
      const res = await axios.post(
        `${API_BASE_URL}/create_profile`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          timeout: 15000,
        }
      );
  
      console.log("📥 [createWorkerProfile] response:", res.data);
      return res.data;
  
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.message ||
        data?.error ||
        err.message ||
        "Ошибка создания профиля";
  
      console.error("❌ [createWorkerProfile] failed:", { status, message, data });
  
      const e = new Error(String(message));
      e.status = status;
      e.data = data;
      throw e;
    }
  },


/**
 * 🔹 Обновляет компанию пользователя через update_profile
 * @param {string} companyId - ID компании
 * @param {string} companyName - Название компании
 */
async updateUserCompany(companyId, companyName) {
  try {
    if (!companyId) throw new Error("companyId не передан");

    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("❌ Пользователь не авторизован");

    const token = await currentUser.getIdToken(true);

    const payload = {
      company_id: companyId,
      company_name: companyName || "",
    };

    console.log("📦 Payload (updateUserCompany):", JSON.stringify(payload, null, 2));

    const response = await axios.post(`${API_BASE_URL}/update_profile`, payload, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });

    console.log("✅ updateUserCompany OK:", response.status, response.data);
    return response.data;
  } catch (error) {
    const msg = error?.response?.data || error?.message || error;
    console.error("❌ Ошибка updateUserCompany:", msg);
    throw error;
  }
},

/**
 * 🔹 PREMIUM — добавление компании в MULTI-COMPANY режим
 * @param {string} companyId 
 * @param {string} companyName 
 */
async updateUserCompanies(companyId, companyName) {
  try {
    if (!companyId) throw new Error("companyId не передан");

    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Пользователь не авторизован");

    const token = await currentUser.getIdToken(true);

    const payload = {
      premium_add_company: true,   // 🔥 ключевой флаг
      company_id: companyId,
      company_name: companyName || ""
    };

    console.log("📦 Payload (updateUserCompanies):", payload);

    const response = await axios.post(`${API_BASE_URL}/update_profile`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    });

    console.log("✅ updateUserCompanies OK:", response.data);
    return response.data;

  } catch (error) {
    console.error("❌ Ошибка updateUserCompanies:", error);
    throw error;
  }
},

async removeUserCompany() {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("❌ Пользователь не авторизован");
    }

    const token = await currentUser.getIdToken(true);

    const payload = {
      remove_fields: ["company_id", "company_name"],
    };

    const response = await axios.post(
      `${API_BASE_URL}/update_profile`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("✅ removeUserCompany OK:", response.status, response.data);
    return response.data;

  } catch (error) {
    console.error(
      "❌ Ошибка при удалении компании:",
      error.response?.data || error.message
    );
    throw error;
  }
},

  async removeUserCompanyIfMultiples(companyId) {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("❌ Пользователь не авторизован");
  
      const token = await currentUser.getIdToken(true);
      if (!token) throw new Error("❌ Токен авторизации не найден");
  
      const payload = {
        action: "remove_company",
        company_id_to_remove: companyId,
      };
  
      console.log("📦 Payload (removeUserCompanyIfMultiples):", JSON.stringify(payload, null, 2));
  
      const response = await axios.post(`${API_BASE_URL}/update_profile`, payload, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });
  
      console.log("✅ removeUserCompanyIfMultiples OK:", response.status, response.data);
      return response.data;
  
    } catch (error) {
      console.error("❌ Ошибка при мульти-удалении компании:", error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Обновляет зарплату (и при желании валюту) через update_profile.
   * @param {number|string} salary — желаемая зарплата
   * @param {string} [currency] — опционально: 'RUB' | 'USD' | 'EUR' и т.д.
   */
  async updateUserSalary(salary, currency) {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("❌ Пользователь не авторизован");

      const numSalary = Number(salary);
      if (!Number.isFinite(numSalary) || numSalary <= 0) {
        throw new Error("Некорректная сумма зарплаты");
      }

      const token = await currentUser.getIdToken(/* forceRefresh */ true);

      const updates = { salary: numSalary };
      if (currency) updates.salary_currency = String(currency).trim();

      const { data } = await axios.post(
        `${API_BASE_URL}/update_profile`,
        { updates },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000,
        }
      );

      if (data?.error) {
        const e = new Error(data.error || "Ошибка при обновлении зарплаты");
        e.code = "SERVER_ERROR";
        e.payload = data;
        throw e;
      }

      console.log("✅ Зарплата успешно обновлена:", data);
      return data; 
    } catch (err) {
      if (err.response) {
        const msg = err.response.data?.message || err.response.data?.error || `HTTP ${err.response.status}`;
        console.error("❌ Ошибка при обновлении зарплаты:", msg, err.response.data);
        const e = new Error(msg);
        e.status = err.response.status;
        e.data = err.response.data;
        throw e;
      }
      console.error("❌ Ошибка при обновлении зарплаты:", err.message);
      throw err;
    }
  },


 async updateUserCity(city) {
  if (!city || typeof city !== "object" || city.id == null) {
    throw new Error("Некорректные параметры: нужен объект city с полем id");
  }

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Пользователь не авторизован");

  const token = await currentUser.getIdToken();

  try {
    const { data } = await axios.post(
      `${API_BASE_URL}/update_profile`,
      { updates: { region: city } }, 
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        timeout: 15000,
      }
    );

    return data;
  } catch (err) {
    const status = err?.response?.status;
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Неизвестная ошибка";
    console.error("❌ Ошибка при обновлении города:", { status, message });
    const e = new Error(String(message));
    e.status = status;
    e.data = err?.response?.data;
    throw e;
  }
},

async completeRegistration() {
  try {
    const { currentUser } = getAuth();
    if (!currentUser) throw new Error("Пользователь не авторизован");
    const token = await currentUser.getIdToken(true);

    const res = await axios.post(
      `${API_BASE_URL}/complete_registration`,
      {}, // пустое тело — ок
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        timeout: 15000,
      }
    );

    console.log("✅ Регистрация завершена:", res.data);
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const data   = err?.response?.data;
    const msg    = data?.message || data?.error || err.message;

    console.error("❌ completeRegistration error:", {
      status,
      msg,
      data,
      headers: err?.response?.headers,
    });

    // Пробрасываем «обогащённую» ошибку
    const e = new Error(String(msg));
    e.status = status;
    e.data   = data;
    throw e;
  }
},

async validateSession(userId) {
  try {
    console.log("✅ начинаем валидацию сессии для user_id:", userId);

    const auth = getAuth();

    const user = await new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe(); 
        resolve(user);
      });
    });

    if (!user) {
      console.warn("⚠️ Пользователь не авторизован, перенаправляем на логин");
      return { isValid: false, isRegistrationComplete: false };
    }

    const token = await user.getIdToken(true);
    localStorage.setItem("auth_token", token);

    const response = await axios.post(`${API_BASE_URL}/validate_session`, {
      user_id: userId,
      token: token
    }, {
      headers: { "Content-Type": "application/json" }
    });

    console.log("✅ Ответ от сервера:", response.data);
    return response.data;

  } catch (error) {
    console.error("❌ Ошибка при валидации сессии:", error.response?.data || error.message);
    return { isValid: false, isRegistrationComplete: false };
  }
},

async getProfile() {
  const auth = getAuth();

  // 1️⃣ Проверяем сразу — есть ли пользователь в Firebase
  if (!auth.currentUser) {
    console.warn("⚠️ Firebase currentUser отсутствует — logout");
    await handleAuthError(401);
    return null;
  }

  const user = auth.currentUser;

  try {
    console.log("📨 Отправляем запрос на get_profile для user_id:", user.uid);

    const token = await user.getIdToken(true);

    const response = await axios.get(`${API_BASE_URL}/get_profile`, {
      params: { 
        user_id: user.uid, 
        full: true      
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      validateStatus: () => true // не бросаем автоматически ошибку
    });

    // 2️⃣ Проверяем статусы авторизации
    if ([401, 403, 404].includes(response.status)) {
      console.warn(`⚠️ get_profile → статус ${response.status}, выполняем logout`);
      await handleAuthError(response.status);
      return null;
    }

    // 3️⃣ Проверяем прочие ошибки
    if (response.status !== 200) {
      throw new Error(`Ошибка сервера: ${response.status}`);
    }

    // 4️⃣ Нормальный ответ
    const data = response.data?.profile;
    if (!data) {
      console.warn("⚠️ Сервер вернул, но поле 'profile' отсутствует:", response.data);
      throw new Error("Профиль не найден");
    }

    const userObj = new User(data);

    console.log("🔥 RAW PROFILE FROM BACKEND:", data);

    console.log("🔥 RAW AUTH FIELDS:", {
      auth_email: data.auth_email,
      provider: data.provider,
    });

    localStorage.setItem("user_profile", JSON.stringify(userObj));

    const city = userObj.city;

    if (city && typeof city === "object") {

      const cityId = city._id ?? city.id ?? city.city_id;

      if (cityId !== null && cityId !== undefined && cityId !== "") {

        const selectedCity = {
          id: String(cityId),
          name: city.city ?? city.name ?? "",
          region_id: city.region_id ?? null,
          region_name: city.region_name ?? "",
        };
        localStorage.setItem(
          "selected_city",
          JSON.stringify(selectedCity)
        );
        console.log("✅ Город сохранён в selected_city:", selectedCity);
      } else {
        console.warn("⚠️ В профиле есть city, но отсутствует ID:", city);
        localStorage.removeItem("selected_city");
      }
    } else {
      console.warn("⚠️ В профиле отсутствует объект city:", city);
      localStorage.removeItem("selected_city");
    }
    console.log("✅ Профиль получен и сохранён в localStorage:", userObj);
    return userObj;

  } catch (error) {
    const msg = error.response?.data || error.message || "Неизвестная ошибка";
    console.error("❌ Ошибка при получении профиля:", msg);

    if (msg.includes("Пользователь не авторизован")) {
      await handleAuthError(401);
      return null;
    }

    throw error;
  }
},
  
  getProfileFromStorage() {
    try {
      const raw = localStorage.getItem("user_profile");
      if (!raw) return null;
      const data = JSON.parse(raw);
      return new User(data);
    } catch (error) {
      console.error("⚠️ Ошибка при чтении профиля из localStorage:", error);
      return null;
    }
  },

  clearProfile() {
    localStorage.removeItem("user_profile");
    console.log("🧹 Профиль успешно очищен из localStorage");
  },

/**
 * 🔹 Загрузка аватара пользователя
 * @param {File} file - Файл изображения
 * @param {boolean} renew - Обновить ли Firestore URL (по умолчанию true)
 * @returns {Promise<Object>}
 */
async uploadUserAvatar(file, renew = true) {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("❌ Пользователь не авторизован");

    const token = await currentUser.getIdToken(true);
    const userId = currentUser.uid;

    const formData = new FormData();
    formData.append("type", "user_avatar");
    formData.append("user_id", userId);
    formData.append("avatar_image", file); // ⚙️ имя поля из upload_image
    formData.append("renew_url", renew ? "true" : "false");

    const response = await axios.post(`${API_BASE_URL}/upload_image`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    console.log("✅ Аватар успешно загружен:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Ошибка при загрузке аватара:", error.response?.data || error.message);
    throw error;
  }
},

/**
 * 🔹 Загружает обложку пользователя
 * @param {string} userId - ID пользователя
 * @param {File} file - файл изображения
 * @returns {Promise<Object>}
 */
  async uploadUserCover(file, renew = true) {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("❌ Пользователь не авторизован");
  
      const token = await currentUser.getIdToken(true);
      const userId = currentUser.uid;
  
      const formData = new FormData();
      formData.append("type", "user_cover");
      formData.append("user_id", userId);
      formData.append("cover_image", file); // ⚙️ имя поля из upload_image
      formData.append("renew_url", renew ? "true" : "false");
  
      const response = await axios.post(`${API_BASE_URL}/upload_image`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      console.log("✅ Обложка успешно загружена:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Ошибка при загрузке обложки:", error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * @param {string} userId
   * @param {Object} updates
   * @returns {Promise<Object>}
   */
  async updateUserProfile(updates) {
    console.log("🟦 [updateUserProfile] входящие данные:", JSON.parse(JSON.stringify(updates)));
  
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("❌ Пользователь не авторизован");
  
    const token = await currentUser.getIdToken(true);
  
    const payload = {
      name: updates.name,
      specifics: updates.specifics,
      phone_number: updates.phone_number,
      email: updates.email,
      about_self: updates.about_self,
      is_phone_number_hidden: updates.is_phone_number_hidden,
      is_remote: updates.is_remote,
      salary: typeof updates.salary === "string"
        ? Number(updates.salary.replace(/\D/g, "")) || undefined
        : updates.salary,
      salary_currency: updates.salary_currency,
      city: updates.city,
      hashtags: updates.hashtags,
    };
  
    // 🚀 LOG: перед очисткой
    console.log("🟩 [updateUserProfile] payload ДО очистки:", JSON.parse(JSON.stringify(payload)));
  
    // удаляем null/undefined
    Object.keys(payload).forEach((k) => {
      if (payload[k] === null || payload[k] === undefined) delete payload[k];
    });
  
    // 🚀 LOG: после очистки
    console.log("🟩 [updateUserProfile] payload ПОСЛЕ очистки:", JSON.parse(JSON.stringify(payload)));
  
    try {
      console.log("📤 [updateUserProfile] отправляем на сервер:", payload);
  
      const res = await axios.post(
        `${API_BASE_URL}/update_profile`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      console.log("📥 [updateUserProfile] ответ сервера:", res.data);
  
      return res.data;
    } catch (error) {
      console.error("❌ [updateUserProfile] ошибка запроса:", error);
      console.error("❌ [updateUserProfile] сервер ответил:", error.response?.data);
      throw error;
    }
  },

/**
 * 🔹 Получает хэштеги текущего пользователя.
 * @param {string} userId - ID пользователя
 * @returns {Promise<Array>} - Массив хэштегов [{ id, name }, ...]
 */
async getMyHashtags(userId) {
  try {
    const response = await axios.get(`${API_BASE_URL}/get_my_hashtags`, {
      params: { user_id: userId },
      headers: { "Content-Type": "application/json" }
    });

    console.log("✅ Ответ от сервера:", response.data);
    return response.data; // 👈 обязательно именно так
  } catch (error) {
    console.error("❌ Ошибка при получении хэштегов:", error);
    throw error;
  }
},

async deleteUserAccount(userId, token) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/delete_user_account`,
      {
        user_id: userId,
        token: token
      }
    );

    const success = response.data?.success === true;

    if (!success) {
      console.error("❌ Сервер не подтвердил удаление:", response.data);
      return false;
    }

    console.log("✅ Аккаунт успешно удалён на сервере");

    // Завершаем Firebase-сессию
    const auth = getAuth();

    try {
      await signOut(auth);
    } catch (e) {
      console.warn("⚠️ Не удалось выполнить signOut:", e);
    }

    // Полностью очищаем локальные данные
    localStorage.clear();
    sessionStorage.clear();

    // Переходим на стартовую страницу
    window.location.replace("/");

    return true;

  } catch (error) {
    console.error(
      "❌ Ошибка при удалении аккаунта:",
      error.response?.data || error.message
    );

    return false;
  }
},

async updateNotifications({ smsMailing, pushMailing }) {
  try {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();

    const payload = {
      updates: {
        sms_mailing: smsMailing,
        push_mailing: pushMailing,
      }
    };

    console.log("📤 Отправляем на update_profile:", payload);

    const response = await axios.post(`${API_BASE_URL}/update_profile`, payload, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    console.log("✅ Ответ update_profile:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Ошибка updateNotifications:", error);
    throw error;
  }
},

// ============================================================
// 🧊 FREEZE ACCOUNT
// ============================================================

async freezeAccount() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Пользователь не авторизован");
  }

  try {
    const token = await user.getIdToken(true);

    const response = await axios.post(
      `${API_BASE_URL}/freeze_account`,
      {
        user_id: user.uid
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },

        validateStatus: () => true,
      }
    );

    if (response.status === 401 || response.status === 403) {
      await handleAuthError(response.status);
      return null;
    }

    if (response.status !== 200) {
      throw new Error(
        response.data?.error ||
        `Ошибка заморозки аккаунта: ${response.status}`
      );
    }

    console.log(
      "🧊 Аккаунт успешно заморожен:",
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      "❌ Ошибка freezeAccount:",
      error.response?.data || error
    );

    throw error;
  }
},


// ============================================================
// ☀️ UNFREEZE ACCOUNT
// ============================================================

async unfreezeAccount() {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Пользователь не авторизован");
  }

  try {
    const token = await user.getIdToken(true);

    const response = await axios.post(
      `${API_BASE_URL}/unfreeze_account`,
      {
        user_id: user.uid
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },

        validateStatus: () => true,
      }
    );

    if (response.status === 401 || response.status === 403) {
      await handleAuthError(response.status);
      return null;
    }

    if (response.status !== 200) {
      throw new Error(
        response.data?.error ||
        `Ошибка восстановления аккаунта: ${response.status}`
      );
    }

    console.log(
      "☀️ Аккаунт успешно восстановлен:",
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      "❌ Ошибка unfreezeAccount:",
      error.response?.data || error
    );

    throw error;
  }
},

async markFirstTutorialAsSeen() {
  try {
    console.log(
      "🎓 Сохраняем просмотр первого tutorial"
    );

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error(
        "Пользователь не авторизован"
      );
    }

    const token = await user.getIdToken(true);

    const response = await axios.post(
      `${API_BASE_URL}/update_profile`,
      {
        updates: {
          is_first_tutorial_was_seen: true,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log(
      "✅ Первый tutorial отмечен как просмотренный:",
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      "❌ Не удалось сохранить просмотр первого tutorial:",
      error.response?.data ||
      error.message ||
      error
    );

    throw error;
  }
},

async markSecondTutorialAsSeen() {
  try {
    console.log(
      "🎓 Сохраняем просмотр второго tutorial"
    );

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error(
        "Пользователь не авторизован"
      );
    }

    const token = await user.getIdToken(true);

    const response = await axios.post(
      `${API_BASE_URL}/update_profile`,
      {
        updates: {
          is_second_tutorial_was_seen: true,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log(
      "✅ Второй tutorial отмечен как просмотренный:",
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      "❌ Не удалось сохранить просмотр второго tutorial:",
      error.response?.data ||
      error.message ||
      error
    );

    throw error;
  }
},

// ============================================================
// 🏢 EMPLOYER CONTENT
// ============================================================

async updateEmployerStatus(isEmployer) {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("Пользователь не авторизован");
    }

    if (typeof isEmployer !== "boolean") {
      throw new Error("isEmployer должен быть boolean");
    }

    const token = await currentUser.getIdToken(true);

    const payload = {
      updates: {
        is_employer: isEmployer
      }
    };

    console.log(
      "🏢 [updateEmployerStatus] Отправляем:",
      payload
    );

    const response = await axios.post(
      `${API_BASE_URL}/update_profile`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        timeout: 15000
      }
    );

    console.log(
      "✅ [updateEmployerStatus] is_employer обновлён:",
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      "❌ [updateEmployerStatus] Ошибка:",
      error?.response?.data ||
      error?.message ||
      error
    );

    throw error;
  }
},

async updateAssumeSpecifics(specificCode) {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("Пользователь не авторизован");
    }

    const normalizedCode = String(specificCode).trim();

    const codeNumber = Number(normalizedCode);

    if (
      !Number.isInteger(codeNumber) ||
      codeNumber < 1 ||
      codeNumber > 15
    ) {
      throw new Error(
        "specificCode должен быть в диапазоне 01...15"
      );
    }

    const assumeSpecific = String(codeNumber).padStart(2, "0");

    const token = await currentUser.getIdToken(true);

    const payload = {
      updates: {
        assume_specifics: assumeSpecific
      }
    };

    console.log(
      "🎯 [updateAssumeSpecifics] Отправляем:",
      payload
    );

    const response = await axios.post(
      `${API_BASE_URL}/update_profile`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        timeout: 15000
      }
    );

    console.log(
      "✅ [updateAssumeSpecifics] assumeSpecifics обновлён:",
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      "❌ [updateAssumeSpecifics] Ошибка:",
      error?.response?.data ||
      error?.message ||
      error
    );

    throw error;
  }
},

};

export default UserService;

async function handleAuthError(status) {
  const auth = getAuth();
  try {
    await signOut(auth);
  } catch (e) {
    console.warn("⚠️ Firebase logout error:", e);
  }

  localStorage.clear();
  console.log(`🚪 Logout (code ${status}) → redirect to /`);

  // Даем чуть времени очиститься Firebase
  setTimeout(() => {
    window.location.href = "/";
  }, 150);
}